import { useEffect } from "react";
import { ASSETS, PRODUCTION_HOST_PATTERN, PRODUCTION_URL, SEO } from "./config";

const HEAD_ATTR = "data-bp-head";

const addTag = (tag, attrs, text) => {
  const el = document.createElement(tag);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  if (text) el.textContent = text;
  el.setAttribute(HEAD_ATTR, "");
  document.head.appendChild(el);
  return el;
};

export const absoluteUrl = (path) => `${PRODUCTION_URL}${path.replace(/^\//, "")}`;

export const ORGANISATION_JSON_LD = {
  "@type": "Organization",
  name: SEO.organisationName,
  legalName: SEO.legalName,
  url: PRODUCTION_URL,
  logo: absoluteUrl(ASSETS.logo),
  description: SEO.description,
};

const HOME_PAGE = {
  title: SEO.title,
  description: SEO.description,
  path: "/",
  ogImage: ASSETS.logo,
  ogImageAlt: "The Business Playwright logo",
  jsonLd: { "@context": "https://schema.org", ...ORGANISATION_JSON_LD },
};

export const usePageSeo = (page = HOME_PAGE) => {
  useEffect(() => {
    const isProduction = PRODUCTION_HOST_PATTERN.test(window.location.hostname);
    const previousTitle = document.title;
    const existingDescription = document.querySelector('meta[name="description"]');
    const previousDescription = existingDescription?.getAttribute("content");
    const existingIcons = Array.from(document.querySelectorAll('link[rel~="icon"]'));
    existingIcons.forEach((el) => el.remove());

    document.title = page.title;
    if (existingDescription) existingDescription.setAttribute("content", page.description);
    else addTag("meta", { name: "description", content: page.description });

    const canonical = absoluteUrl(page.path);
    const ogImage = absoluteUrl(page.ogImage);
    const tags = [
      ["meta", { name: "robots", content: isProduction ? "index,follow" : "noindex,nofollow" }],
      ["link", { rel: "canonical", href: canonical }],
      ["meta", { property: "og:type", content: "website" }],
      ["meta", { property: "og:site_name", content: SEO.organisationName }],
      ["meta", { property: "og:title", content: page.title }],
      ["meta", { property: "og:description", content: page.description }],
      ["meta", { property: "og:url", content: canonical }],
      ["meta", { property: "og:image", content: ogImage }],
      ["meta", { property: "og:image:alt", content: page.ogImageAlt }],
      ["meta", { name: "twitter:card", content: "summary_large_image" }],
      ["meta", { name: "twitter:title", content: page.title }],
      ["meta", { name: "twitter:description", content: page.description }],
      ["meta", { name: "twitter:image", content: ogImage }],
      ["meta", { name: "theme-color", content: "#F6F3ED" }],
      ["link", { rel: "icon", type: "image/png", sizes: "32x32", href: ASSETS.favicon32 }],
      ["link", { rel: "icon", type: "image/png", sizes: "192x192", href: ASSETS.favicon192 }],
      ["link", { rel: "apple-touch-icon", sizes: "180x180", href: ASSETS.appleTouchIcon }],
    ];
    const added = tags.map(([tag, attrs]) => addTag(tag, attrs));
    added.push(addTag("script", { type: "application/ld+json" }, JSON.stringify(page.jsonLd)));

    return () => {
      document.title = previousTitle;
      if (existingDescription && previousDescription != null) existingDescription.setAttribute("content", previousDescription);
      added.forEach((el) => el.remove());
      existingIcons.forEach((el) => document.head.appendChild(el));
    };
  }, [page]);
};
